export type CapabilityMap = {
  canUsePersonalPlanner: boolean;
  canCreateOrganization: boolean;
  canInviteEmployees: boolean;
  canManageTeams: boolean;
  canRequestLeave: boolean;
  canApproveLeave: boolean;
  canViewTeamPlanning: boolean;
  canViewOrganizationReports: boolean;
  canManageOrganizationPolicies: boolean;
  canExportOrganizationData: boolean;
  canUseMultipleTeams: boolean;
  canUseMultipleManagers: boolean;
  canUseDepartments: boolean;
  canUseLocations: boolean;
  canUseExtendedAudit: boolean;
  organizationReadOnly: boolean;
};

export type Organization = {
  id: number;
  public_id: string;
  name: string;
  slug: string;
  role: "employee" | "manager" | "org_admin" | "owner";
  package: "team" | "business" | null;
  subscription_status: string | null;
  seat_limit: number | null;
  active_seats: number;
  capabilities: CapabilityMap;
};

export type BalanceSummary = {
  leave_type_id: number;
  leave_type: string;
  leave_type_code: string;
  category: string;
  current_minutes: number;
  current_hours: number;
  transport_minutes: number;
  transport_hours: number;
  newly_awarded_minutes: number;
  newly_awarded_hours: number;
  approved_minutes: number;
  approved_hours: number;
  requested_minutes: number;
  requested_hours: number;
  planned_minutes: number;
  planned_hours: number;
  pending_credit_minutes: number;
  pending_credit_hours: number;
  planned_credit_minutes: number;
  planned_credit_hours: number;
  available_minutes: number;
  available_hours: number;
  opening_minutes?: number;
  opening_hours?: number;
  credited_minutes?: number;
  credited_hours?: number;
  debited_minutes?: number;
  debited_hours?: number;
  projected_end_minutes?: number;
  projected_end_hours?: number;
};

export type EmploymentContract = {
  id: number;
  employment: number;
  contract_type: "permanent" | "fixed_term";
  start_date: string;
  end_date: string | null;
  label: string;
  contract_hours_per_week: string;
  fulltime_hours_per_week: string | null;
  fulltime_hours: string;
  arbeidsfactor: string;
  arbeidsfactor_percentage: number;
  created_at: string;
  updated_at: string;
};

export type EmploymentRelationship = {
  id: number;
  user: number;
  user_name: string;
  user_email: string;
  organization: number | null;
  employer_name: string;
  fulltime_hours_per_week: string;
  combine_atv_with_leave: boolean;
  display_name: string;
  contracts: EmploymentContract[];
  created_at: string;
};

export type BalanceLayer = {
  bucket_id: number;
  leave_type: string;
  leave_type_code: string;
  source_year: number;
  remaining_minutes: number;
  remaining_hours: number;
  expires_on: string;
};

export type AccrualPolicy = {
  id: number;
  user: number;
  organization: number | null;
  employment: number | null;
  leave_type: number;
  leave_type_name: string;
  leave_type_category: string;
  frequency: "monthly" | "yearly" | "manual";
  unit: "hours";
  fulltime_annual_hours: string;
  fulltime_annual_hours_value: number;
  fulltime_day_hours: string;
  prorated_by_contract: boolean;
  validity_year: number | null;
  expires_on: string | null;
  start_date: string;
  prorate_on_start: boolean;
  annual_minutes: number;
  annual_hours: number;
  calculation: {
    fulltime_annual_hours: number;
    fulltime_annual_minutes: number;
    fulltime_hours_per_week: number;
    contract_hours_per_week: number | null;
    arbeidsfactor: number;
    arbeidsfactor_percentage: number;
    personal_annual_minutes: number;
    personal_annual_hours: number;
  } | null;
  active: boolean;
  grants_count: number;
};

export type WorkContractOverview = {
  id: number;
  contract_type: "permanent" | "fixed_term";
  start_date: string;
  end_date: string | null;
  label: string;
  contract_hours_per_week: number;
  fulltime_hours_per_week: number | null;
  arbeidsfactor: number;
  arbeidsfactor_percentage: number;
};

export type WorkScheduleOverview = {
  id: number;
  label: string;
  effective_from: string;
  effective_until: string | null;
  schedule_type: string;
  total_minutes_per_week: number;
};

export type WorkPolicyOverview = {
  id: number;
  leave_type: number;
  leave_type_name: string;
  category: string;
  unit: "hours";
  fulltime_annual_hours: number;
  fulltime_day_hours: string;
  prorated_by_contract: boolean;
  validity_year: number | null;
  expires_on: string | null;
  start_date: string;
  prorate_on_start: boolean;
  annual_minutes: number;
  annual_hours: number;
  calculation: AccrualPolicy["calculation"];
  balance: BalanceSummary | null;
};

export type WorkEmploymentOverview = {
  id: number;
  organization_id: number | null;
  name: string;
  fulltime_hours_per_week: number;
  combine_atv_with_leave: boolean;
  contracts: WorkContractOverview[];
  schedules?: WorkScheduleOverview[];
  policies?: WorkPolicyOverview[];
};

export type WorkOverview = {
  year: number;
  user: number;
  organization: number | null;
  employment: number | null;
  fulltime_hours_per_week: number | null;
  employments: WorkEmploymentOverview[];
  schedules: WorkScheduleOverview[];
  policies: WorkPolicyOverview[];
};

export type LeaveRecalculation = {
  scope: "personal" | "organization";
  pool_enabled: boolean;
  organization: number | null;
  employment: number | null;
  years: Array<{ year: number; entry_count: number; minutes: number }>;
};

export type ExpiryPolicy = {
  id: number;
  user: number;
  organization: number | null;
  employment: number | null;
  leave_type: number;
  leave_type_name: string;
  expiry_months: number;
  carry_over_max_minutes: number | null;
  carry_over_max_hours: number | null;
};

export type BalanceMutation = {
  id: number;
  organization: number | null;
  employment: number | null;
  leave_type: number;
  leave_type_name: string;
  bucket: number | null;
  entry: number | null;
  reason: string;
  minutes_delta: number;
  date: string | null;
  source: string;
  reference_id: string;
  description: string;
  balance_before: number | null;
  balance_after: number | null;
  metadata: Record<string, unknown>;
  can_reverse: boolean;
  created_at: string;
};

export type OrganizationMember = {
  id: number;
  user: number;
  user_email: string;
  display_name: string;
  role: string;
  status: string;
  joined_at: string;
};

export type CalendarFeed = {
  id: number;
  name: string;
  token: string;
  feed_path: string;
  scope: "all" | "personal" | "organization";
  organization: number | null;
  statuses: Array<"draft" | "requested" | "approved">;
  detail_level: "detailed" | "free_busy";
  leave_types: number[];
  leave_type_names: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type LeaveLayer = {
  id: number;
  status: LeaveStatus;
  status_label: string;
  leave_type: { id: number; name: string; category: string };
  employment?: number | null;
  requested_minutes: number;
  duration_mode: string;
};

export type EmploymentScheduleDay = {
  employment_id: number;
  employment_name: string;
  scheduled_minutes: number;
  shift_label: string;
};

export type CalendarDay = {
  date: string;
  weekday: number;
  week: number;
  scheduled_minutes: number;
  schedule_source: string;
  shift_label: string;
  employment_schedules?: EmploymentScheduleDay[];
  flags: {
    is_school_holiday: boolean;
    has_leave: boolean;
    is_public_holiday: boolean;
  };
  layers: {
    school_holidays?: Array<{
      id: number;
      key?: string;
      event_type?: "vacation" | "study_day";
      name: string;
      display_name?: string;
      region: string;
      start_date?: string;
      end_date?: string;
      source?: "government" | "manual";
      school_id?: number | null;
      school_name?: string;
      part?: "whole_day" | "morning" | "afternoon";
      scope?: "whole_school" | "specific_groups";
    }>;
    public_holidays?: Array<{ id: number; name: string; is_optional?: boolean }>;
    leave?: LeaveLayer[];
    events?: Array<{
      id: number;
      event_type: string;
      title: string;
      annual_recurrence: boolean;
      organization: number | null;
    }>;
    notes?: Array<{ id: number; text: string }>;
  };
};

export type CalendarResponse = {
  start_date: string;
  end_date: string;
  school_region: "north" | "middle" | "south" | "";
  school_regions?: Array<"north" | "middle" | "south">;
  has_personal_schools?: boolean;
  school_holidays_are_informational: true;
  days: CalendarDay[];
  results: LeaveEntry[];
};

export type School = {
  id: number;
  name: string;
  region: "north" | "middle" | "south" | null;
  region_label: string;
  created_at: string;
  updated_at: string;
};

export type SchoolVacation = {
  id: number;
  school_year: string;
  region: "north" | "middle" | "south";
  name: string;
  start_date: string;
  end_date: string;
};

export type SchoolHoliday = {
  id: number;
  school: number;
  name: string;
  start_date: string;
  end_date: string;
};

export type SchoolGroup = {
  id: number;
  school: number;
  name: string;
};

export type SchoolChild = {
  id: number;
  school: number;
  group: number;
  group_name: string;
  name: string;
};

export type SchoolStudyDay = {
  id: number;
  school: number;
  name: string;
  date: string;
  scope: "whole_school" | "specific_groups";
  scope_label: string;
  part: "whole_day" | "morning" | "afternoon";
  part_label: string;
  groups: number[];
};

export type Holiday = {
  id: number;
  date: string;
  name: string;
  is_optional: boolean;
  country_code: string;
  source: "national" | "custom";
  code: string;
  annual_recurrence: boolean;
  organization: number | null;
  user: number | null;
};

export type LeaveStatus =
  | "draft"
  | "requested"
  | "needs_info"
  | "approved"
  | "rejected"
  | "cancelled";

export type LeaveEntry = {
  id: number;
  user: number;
  user_name: string;
  user_email: string;
  organization: number | null;
  employment: number | null;
  team: number | null;
  leave_type: number;
  leave_type_name: string;
  start_date: string;
  end_date: string;
  input_unit: "hours" | "days";
  input_value: string;
  duration_mode: "whole_day" | "partial_day" | "manual";
  requested_minutes: number;
  approved_minutes: number;
  overtime_factor: string;
  status: LeaveStatus;
  reason: string;
  note: string;
  request_days: Array<{
    date: string;
    scheduled_minutes: number;
    requested_minutes: number;
    public_holiday_name: string;
    school_holiday_names: string[];
  }>;
};

export type LeaveType = {
  id: number;
  code: string;
  name: string;
  category: string;
  default_overtime_factor: string;
  consumes_overtime_bucket: boolean;
  is_active: boolean;
  requires_balance: boolean;
  requires_approval: boolean;
};

export type Team = {
  id: number;
  organization: number;
  name: string;
  memberships: Array<{
    id: number;
    user: number;
    user_email: string;
    role: string;
    privacy_level: string;
  }>;
};

export type Paginated<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

export function unwrapResults<T>(payload: Paginated<T> | T[]): T[] {
  return Array.isArray(payload) ? payload : payload.results;
}
